import { useQuery } from 'react-query'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { Add } from '@mui/icons-material'
import { Button, Card, DeleteIcon, EditIcon, Table } from 'components'
import { TableActionConfig, TableHeaderType } from 'components/Table/types'
import { siteDetailsTableColumns } from './siteDetailsColumns'
import useAddEditCompanyModal from './dialogs/useAddEditCompanyModal'
import useDeleteCompanyModal from './dialogs/useDeleteModal'
import { Company } from '../../../../types'
import { CrudAction, Entity } from '../../../../types/permissions'
import {
  usePageHeader,
  usePagination,
  usePermissions,
} from '../../../../utils/hooks'
import { useCompaniesService } from '../../../../services'
import { TableContainer } from './SiteDetails.styled'

const SiteDetails = () => {
  const { checkPermission } = usePermissions()
  const { getCompanies } = useCompaniesService()

  const {
    currentPage: sitesCurrentPage,
    setCurrentPage: setSitesCurrentPage,
    rowsPerPage: sitesRowsPerPage,
    setRowsPerPage: setSitesRowsPerPage,
  } = usePagination({ tableKey: 'sites' })

  const { data, isLoading } = useQuery({
    queryKey: ['fetch-companies', sitesCurrentPage, sitesRowsPerPage],
    queryFn: () =>
      getCompanies({
        currentPage: sitesCurrentPage,
        itemsPerPage: sitesRowsPerPage,
      }),
    retry: false,
  })

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'))

  const [addModal, toggleAddEditModal] = useAddEditCompanyModal({
    data: {
      companies: data?.results,
    },
  })

  const [deleteModal, toggleDeleteModal] = useDeleteCompanyModal({
    onConfirm: () => {
      console.log('confirmed')
    },
  })

  usePageHeader({
    headerTitle: 'Site List',
    actionButton: (
      <Button
        startIcon={<Add />}
        onClick={() => toggleAddEditModal(undefined)}
        disabled={!checkPermission(Entity.COMPANIES, CrudAction.CREATE)}
      >
        Add Site
      </Button>
    ),
  })

  const onSitesPaginationChange = (page: number, perPage: number) => {
    setSitesCurrentPage(page)
    setSitesRowsPerPage(perPage)
  }

  const actions: TableActionConfig<Company>[] = [
    {
      name: 'edit',
      icon: <EditIcon size={14} />,
      handler: (row: Company) => {
        if (!row) return
        toggleAddEditModal(row)
      },
    },
    {
      name: 'delete',
      icon: <DeleteIcon size={20} />,
      handler: (row: Company) => {
        if (!row?.id) return
        toggleDeleteModal(row.id)
      },
      permission: {
        entity: Entity.COMPANIES,
        action: CrudAction.DELETE,
      },
    },
  ]

  return (
    <Box>
      <Card>
        {data && !isLoading && (
          <TableContainer
            sx={{
              height: isSmallScreen ? 'unset' : 'calc(100vh - 120px)',
            }}
          >
            <Table
              data={data?.results}
              columns={siteDetailsTableColumns({
                actions,
              })}
              headerType={TableHeaderType.Card}
              showPagination={true}
              onPaginationChange={onSitesPaginationChange}
              total={data?.pagination?.total || 0}
              tableKey="sites"
            />
          </TableContainer>
        )}
      </Card>
      {addModal}
      {deleteModal}
    </Box>
  )
}

export default SiteDetails
